import { FC, useCallback, useEffect, useRef, type JSX } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import useCreateProposal from '@/api/mutations/proposals/useCreateProposal';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { useProposalTemplateChoice } from '@/hooks/useProposalTemplateChoice';
import CreateProposalTemplateModal from '@/ui/components/createProposalTemplateModal/CreateProposalTemplateModal';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { PublicHydratedEntityTemplate } from '@payaca/types/entity-templates';

/**
 * Landing page for creating a v2 Proposal from a Project. Lets the user pick a
 * template (or start from scratch) before creating the Proposal and navigating
 * into the v2 builder. Reached at `projects/:projectId/proposals/new`, where
 * `:projectId` is the Project's public id.
 */
const NewProposalPage: FC = (): JSX.Element => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();

  const { project } = useGetProjectByIdOverview(projectId);
  const { hasCheckedTemplates, hasAnyTemplates, showTemplateSelectionModal } =
    useProposalTemplateChoice();
  const { mutate: createProposal, isPending } = useCreateProposal();

  const hasTriggeredCreate = useRef(false);

  const proposalsPath = `/projects/${project?.publicId}/proposals`;

  const create = useCallback(
    (template?: PublicHydratedEntityTemplate<'proposal'>) => {
      if (!project || hasTriggeredCreate.current) {
        return;
      }
      hasTriggeredCreate.current = true;
      createProposal(
        {
          projectId: project.id,
          templatePublicId: template?.publicId,
        },
        {
          onSuccess: ({ createProposal: created }) => {
            history.replace(
              `/projects/${project.publicId}/proposals/${created.publicId}`
            );
          },
          // The global mutation error toast surfaces the failure; return the
          // user to the Project rather than stranding them on this page.
          onError: () => {
            hasTriggeredCreate.current = false;
            history.replace(`/projects/${project.publicId}/proposals`);
          },
        }
      );
    },
    [project, createProposal, history]
  );

  // Once the project has loaded and the template check has resolved, auto-create
  // a blank Proposal when there's no template selection to make.
  useEffect(() => {
    if (!project || !hasCheckedTemplates || showTemplateSelectionModal) {
      return;
    }
    create();
  }, [project, hasCheckedTemplates, showTemplateSelectionModal, create]);

  // The template check can resolve before the project loads; only open the
  // modal once the project is available so its actions have a target.
  const isModalOpen = !!project && showTemplateSelectionModal && !isPending;

  return (
    <AuthenticatedPageWrapper>
      <CreateProposalTemplateModal
        isOpen={isModalOpen}
        hasAnyTemplates={hasAnyTemplates}
        onApplyTemplate={(template) => create(template)}
        onClose={() => history.push(proposalsPath)}
      />
      {!isModalOpen && <LoaderOverlay />}
    </AuthenticatedPageWrapper>
  );
};

export default NewProposalPage;
